import { Components, Theme } from '@mui/material'
import { insetBorder } from './insetBorder'

declare module '@mui/material/TextField' {
  interface BaseTextFieldProps {
    'data-is-ai-prefilled'?: boolean
    // Not sure if this works...
    variant: 'standard' // We only support the standard variant in theme2, so we can add it to the props to make it easier to use. The outlined variant is more complex to override and we don't use it in our designs, so we'll just exclude it.
  }
  // Only the standard variant is supported in theme2.
  // TODO - see if there's a way to enforce this with types
}

const aiPrefillStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.futureTheme.bg.utility.plum.muted,
  boxShadow: insetBorder({
    width: theme.futureTheme.strokeWidth.default,
    color: theme.palette.futureTheme.border.utility.plum.muted,
  }),
  '& .MuiInput-input': {
    color: theme.palette.futureTheme.text.utility.plum.default,
  },
  '&:hover': {
    boxShadow: insetBorder({
      width: theme.futureTheme.strokeWidth.default,
      color: theme.palette.futureTheme.border.utility.plum.strongest,
    }),
  },
  '&.Mui-focused': {
    boxShadow: `${insetBorder({
      width: theme.futureTheme.strokeWidth.default,
      color: theme.palette.futureTheme.border.utility.plum.strongest,
    })}, ${theme.futureTheme.shadows.activeAi}`,
  },
})

export const MuiTextField: Components<Omit<Theme, 'components'>>['MuiTextField'] = {
  defaultProps: {
    variant: 'standard',
    size: 'medium',
  },
  variants: [
    {
      // AI Prefill state
      // This only ends up on the TextField, not the input
      props: { 'data-is-ai-prefilled': true },
      style: ({ theme }) => ({
        '& .MuiInput-root:not(.Mui-disabled)': aiPrefillStyle(theme),
      }),
    },
  ],
}

export const MuiInputBase: Components<Omit<Theme, 'components'>>['MuiInputBase'] = {
  styleOverrides: {
    input: ({ theme }) => ({
      '&.Mui-disabled': {
        color: theme.palette.futureTheme.text.default,
        '-webkit-text-fill-color': theme.palette.futureTheme.text.default,
      },
    }),
  },
}

export const MuiInput: Components<Omit<Theme, 'components'>>['MuiInput'] = {
  defaultProps: {
    // notched: false,
  },
  styleOverrides: {
    underline: {
      '&::before, &::after': {
        display: 'none',
      },
    },
    root: ({ theme }) => ({
      // Override MUI default spacing, we've got ours on the label
      'label + &': {
        marginTop: 0,
      },
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
      borderRadius: theme.shape['futureTheme.radius-lg'],
      boxShadow: insetBorder({
        width: theme.futureTheme.strokeWidth.default,
        color: theme.palette.futureTheme.border.strong,
      }),
      '& .MuiOutlinedInput-notchedOutline': {},
      '&:not(.Mui-disabled, .Mui-focused):hover': {
        boxShadow: insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.strongest,
        }),
      },
      '&.Mui-focused': {
        boxShadow: `${insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.strongest,
        })}, ${theme.futureTheme.shadows.activeFocus}`,
      },

      '&.Mui-error': {
        backgroundColor: theme.palette.futureTheme.bg.danger.muted,
        boxShadow: insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.danger.strong,
        }),
        '&:hover': {
          boxShadow: insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.danger.strongest,
          }),
        },
        '&.Mui-focused': {
          boxShadow: `${insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.danger.strongest,
          })}, ${theme.futureTheme.shadows.activeError}`,
        },
        '& .MuiInput-input': {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
        },
      },

      '&.MuiInputBase-colorSuccess': {
        backgroundColor: theme.palette.futureTheme.bg.success.muted,
        boxShadow: insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.success.subtle,
        }),
        '&:hover': {
          boxShadow: insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.success.strongest,
          }),
        },
        '&.Mui-focused': {
          boxShadow: `${insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.success.strongest,
          })}, ${theme.futureTheme.shadows.activeSuccess}`,
        },
        '& .MuiInput-input': {
          color: theme.palette.futureTheme.text.success.onSuccessMuted,
        },
        // colorSuccess doesn't get applied to the MuiFormHelperText, so we have to target it with a sibling selector
        '& ~ .MuiFormHelperText-root': {
          color: theme.palette.futureTheme.text.success.default,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: theme.palette.futureTheme.bg.surface.subtle,
        '& .MuiInput-input': {
          // Disabled text
          '-webkit-text-fill-color': theme.palette.futureTheme.text.default,
        },
      },

      '&[data-is-ai-prefilled="true"]': aiPrefillStyle(theme),

      // Sizes
      variants: [
        {
          props: { size: 'small' },
          style: {
            padding: theme.spacing(1.5, 2.5, 1.5, 3),
          },
        },
        {
          props: { size: 'medium' },
          style: {
            padding: theme.spacing(2, 2.5, 2, 3),
          },
        },
      ],
    }),

    input: ({ theme }) => ({
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,
      padding: 0,

      // Designs use Typography/LineHeight/base = 24px
      // But line-height doesn't really work for inputs, so minHeight
      minHeight: '24px',

      '&::placeholder': {
        ...theme.typography['futureTheme.body-regular'],
        opacity: 1,
        color: theme.palette.futureTheme.text.subtle,
      },
    }),
  },
}

export const MuiInputLabel: Components<Omit<Theme, 'components'>>['MuiInputLabel'] = {
  defaultProps: {
    shrink: true, // We always want the label shrunk (floating) because we don't use the native placeholder.
    margin: 'dense', // Tighter spacing by default, since the label is always floating.
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // Combined with shrink: true, this makes the label sit statically above the input, rather than floating
      position: 'relative',
      transform: 'unset',

      // Styling
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,
      // Don't change label colour on focus, error, disabled
      '&.Mui-focused, &.Mui-error, &.Mui-disabled': {
        color: theme.palette.futureTheme.text.default,
      },

      // Flex so that we can put InfoTooltip after the asterisk
      display: 'flex',
      alignItems: 'center',
      '& .information-tooltip': {
        order: 100,
        marginLeft: theme.spacing(0.5),
      },

      marginBottom: theme.spacing(1.5),
    }),
    asterisk: ({ theme }) => ({
      color: theme.palette.futureTheme.text.danger.default,
    }),
  },
}
export const MuiFormHelperText: Components<Omit<Theme, 'components'>>['MuiFormHelperText'] = {
  defaultProps: {
    margin: 'dense',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      marginLeft: 0,
      marginTop: theme.spacing(1.5),
      ...theme.typography['futureTheme.small-regular'],
      color: theme.palette.futureTheme.text.subtle,
      '&.Mui-error': {
        color: theme.palette.futureTheme.text.danger.default,
      },
    }),
  },
}

export const MuiInputAdornment: Components<Omit<Theme, 'components'>>['MuiInputAdornment'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.futureTheme.fg.subtle,
      // Without these, occasionally the adornment can overflow
      minWidth: 0,
      flexShrink: 0,
      '&.MuiInputAdornment-positionEnd .MuiIconButton-root': {
        // Offset the icon button spacing - equivalent to (and overrides) edge="end"
        marginRight: theme.spacing(-0.5),
      },
      // MUI wraps string adornments in a Typography component
      '& .MuiTypography-root': {
        ...theme.typography['futureTheme.body-regular'],
        color: theme.palette.futureTheme.text.subtle,
      },
    }),
  },
}
